/**
 * Правка значения по клику прямо в ячейке таблицы.
 *
 * Основной способ работы во всех справочниках: техник правит телефон врача или
 * цену позиции, не открывая карточку. Форма ради одного поля здесь была бы
 * лишним шагом на каждое действие.
 *
 * Поведение перенесено из демо дословно:
 *   - клик по значению или по карандашу открывает поле;
 *   - Enter подтверждает, Escape отменяет, клик мимо отменяет;
 *   - пустая строка НЕ сохраняется для текста, пока не разрешено явно
 *     стирали бы название), но для чисел ноль — допустимое значение.
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { useAutoFocus } from './useAutoFocus.js';

export function EditableText({
  value,
  onSave,
  type = 'text',
  textStyle,
  textClassName,
  inputWidth = 160,
  placeholder,
  suffix,
  disabled = false,
  multiline = false,
  allowEmpty = false,
}: {
  value: string | number;
  onSave: (value: string | number) => void;
  type?: 'text' | 'number';
  textStyle?: CSSProperties;
  textClassName?: string;
  inputWidth?: number;
  placeholder?: string;
  suffix?: string;
  disabled?: boolean;
  multiline?: boolean;
  /**
   * Разрешить сохранить ПУСТОЕ значение.
   *
   * Нужно там, где пустота — обычное состояние поля: описание позиции для
   * прайса чаще всего не заполнено вовсе. Названию это давать нельзя:
   * случайный Enter стёр бы его насовсем.
   */
  allowEmpty?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string | number>(value);
  const containerRef = useRef<HTMLSpanElement>(null);
  const inputRef = useAutoFocus<HTMLInputElement>(editing);

  const start = () => {
    if (disabled) return;
    setDraft(value);
    setEditing(true);
  };

  const confirm = () => {
    if (type === 'number') {
      onSave(Number(draft) || 0);
    } else {
      const text = String(draft).trim();
      /*
       * Пустое значение по умолчанию НЕ сохраняется: случайный Enter не
       * должен стирать название позиции.
       *
       * Но там, где пустота — обычное состояние (описание для прайса),
       * стирать надо давать: набранное на пробу слово иначе остаётся в
       * прайсе навсегда, и убрать его нечем.
       */
      if (text || allowEmpty) onSave(text);
    }
    setEditing(false);
  };

  const cancel = () => setEditing(false);

  useEffect(() => {
    if (!editing) return;
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      cancel();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [editing]);

  if (editing) {
    const Input = multiline ? 'textarea' : 'input';
    return (
      /*
        Ряд правки ПЕРЕНОСИТСЯ и не шире своей ячейки.

        Поле числа было шириной ровно 90 точек, а рядом стояли знак рубля и две
        кнопки — вместе около 165. В карточке справочника на телефоне колонка
        цены уже 110, и ряд вылезал на соседнюю: кнопки ложились поверх поля
        «Договор», и попасть в него было нельзя.
      */
      <span className="inline-flex w-full min-w-0 flex-wrap items-center gap-1" ref={containerRef}>
        <Input
          // Фокус без прокрутки: правка в длинной таблице не должна
          // перебрасывать список к началу.
          ref={inputRef as never}
          {...(multiline ? { rows: 2 } : { type })}
          value={String(draft)}
          onChange={(e: { target: { value: string } }) => setDraft(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter' && !multiline) confirm();
            if (event.key === 'Escape') cancel();
          }}
          className="min-w-0 flex-1 rounded border px-1.5 py-0.5 text-sm"
          style={{
            borderColor: 'var(--otc-accent)',
            /*
              Ширина — ПРЕДЕЛ, а не размер. В широкой таблице поле остаётся
              прежним, в узкой карточке сжимается до ячейки вместо того,
              чтобы вылезать за её край.
            */
            maxWidth: type === 'number' ? 110 : inputWidth,
            fontFamily: type === 'number' ? "'IBM Plex Mono', monospace" : undefined,
          }}
        />
        {suffix && <span className="text-xs text-ink2">{suffix}</span>}
        <button
          onClick={confirm}
          className="flex-shrink-0 rounded p-1"
          style={{ background: 'var(--otc-accent)' }}
          title="Подтвердить"
        >
          <Check size={11} color="#fff" />
        </button>
        <button onClick={cancel} className="flex-shrink-0 rounded p-1 hover:bg-black/5" title="Отмена">
          <X size={11} className="text-ink2" />
        </button>
      </span>
    );
  }

  const empty = value === '' || value === null || value === undefined;

  return (
    <span className="group inline-flex items-center gap-1.5">
      <span
        className={`${textClassName ?? ''} ${disabled ? '' : 'cursor-pointer'}`}
        style={textStyle}
        onClick={disabled ? undefined : start}
      >
        {empty ? placeholder : value}
        {/*
          Пробел перед знаком НЕРАЗРЫВНЫЙ. Обычный позволял переносить «₽»
          на следующую строку отдельно от суммы — в узких колонках справочника
          так выглядела каждая цена. Само число не рвётся и без того: разряды
          в нём разделены неразрывным пробелом из русской локали.
        */}
        {suffix && !empty ? ` ${suffix}` : ''}
      </span>
      {!disabled && (
        <button
          onClick={start}
          className="flex-shrink-0 rounded p-0.5 opacity-0 transition-opacity hover:bg-black/5 focus:opacity-100 group-hover:opacity-100"
          title="Изменить"
        >
          <Pencil size={11} className="text-ink2" />
        </button>
      )}
    </span>
  );
}
